/**
 * Typed client for the onboarding routes (onboardingRoutes in @bs/contracts). Same transport as the other
 * module clients (cookie session, CSRF header, ErrorEnvelope -> ApiError); every response is checked
 * against the contract before the UI sees it.
 */
import type { z } from 'zod';
import { onboardingRoutes, type OnboardingRouteName } from '@bs/contracts';
import { ApiError, buildPath } from '../api/client.js';
import { send } from '../modules/products/pl-api.js';

type R = typeof onboardingRoutes;
export type OnbOut<K extends OnboardingRouteName> = z.output<R[K]['response']>;
type BodyIn<K extends OnboardingRouteName> = R[K] extends { body: z.ZodTypeAny } ? z.input<R[K]['body']> : undefined;

export async function onbCall<K extends OnboardingRouteName>(name: K, opts: { params?: Record<string, string>; body?: BodyIn<K> } = {}): Promise<OnbOut<K>> {
  const route = onboardingRoutes[name];
  const res = await send(route.method, buildPath(route.path, opts.params), { body: opts.body });
  let json: unknown;
  try { json = await res.json(); } catch (e) {
    console.error('onboarding: response is not JSON', e);
    throw new ApiError(res.status, 'BAD_RESPONSE', 'The server sent a response that is not JSON.');
  }
  const parsed = (route.response as z.ZodTypeAny).safeParse(json);
  if (!parsed.success) {
    console.error(`onboarding ${name}: response did not match the contract`, parsed.error.issues.slice(0, 3));
    throw new ApiError(res.status, 'BAD_RESPONSE', `The server response for ${name} did not match the contract.`);
  }
  return parsed.data as OnbOut<K>;
}

export const ONB_KEY = ['onboarding'] as const;
